import type { FC } from "react";

import { ResponsiveImage } from "@ui/components/ResponsiveImage";
import { SwiperJti } from "@ui/components/SwiperJti";
import classnames from "classnames";
import dompurify from "isomorphic-dompurify";
import { SwiperSlide } from "swiper/react";

import { LongArrowForwardIcon } from "@jti/brand-icons/long-arrow-forward";
import { Button } from "@jti/core-components/components/Button";

import type { imageGalleryType } from "../types";

import styles from "./ImageGalleryTopic.module.scss";

const { sanitize } = dompurify;

export type ImageGalleryTopicProps = {
  data: imageGalleryType;
  mode?: "dark" | "light";
  rtl?: boolean;
};

export const ImageGalleryTopic: FC<ImageGalleryTopicProps> = ({ data, mode = "light", rtl }) => {
  const imageGalleryTopicClassNames = classnames(styles.imageGalleryTopic, {
    [styles.imageGalleryTopicDark]: mode === "dark",
    [styles.imageGalleryTopicRtl]: rtl,
  });

  return (
    <div className={imageGalleryTopicClassNames} data-testid="image-gallery-topic">
      <div className={styles.imageContainer}>
        {data.title && <div className={styles.title} dangerouslySetInnerHTML={{ __html: sanitize(data.title) }} />}
        <ResponsiveImage
          largeSrc={data.topRightImageDesktop}
          mediumSrc={data.topRightImageTablet}
          smallSrc={data.topRightImageMobile}
        />
      </div>
      <SwiperJti
        mode={mode}
        rtl={rtl}
        swiperJtiClass={styles.imageGalleryTopicSwiper}
        swiperJtiWrapper={styles.imageGalleryTopicSwiperWrapper}
        isShowNavigationArrows={false}
        autoHeight={false}
      >
        <SwiperSlide className={styles.imageGalleryTopicSwiperSlide}>
          <ResponsiveImage
            largeSrc={data.topRightImageDesktop}
            mediumSrc={data.topRightImageTablet}
            smallSrc={data.topRightImageMobile}
          />
        </SwiperSlide>
        <SwiperSlide className={styles.imageGalleryTopicSwiperSlide}>
          <ResponsiveImage
            largeSrc={data.bottomLeftImageDesktop}
            mediumSrc={data.bottomLeftImageTablet}
            smallSrc={data.bottomLeftImageMobile}
          />
        </SwiperSlide>
      </SwiperJti>
      <div className={classnames(styles.imageContainer, styles.content)}>
        <ResponsiveImage
          largeSrc={data.bottomLeftImageDesktop}
          mediumSrc={data.bottomLeftImageTablet}
          smallSrc={data.bottomLeftImageMobile}
        />
        {(data.subtitle || data.descriptiveText || data.ctaText) && (
          <div className={styles.info}>
            {data.subtitle && (
              <div className={styles.subtitle} dangerouslySetInnerHTML={{ __html: sanitize(data.subtitle) }} />
            )}
            {data.descriptiveText && (
              <div
                className={styles.descriptiveText}
                dangerouslySetInnerHTML={{ __html: sanitize(data.descriptiveText) }}
              />
            )}
            {data.ctaText && (
              <Button
                variant="text-muted"
                size="sm"
                data-testid="arrow-link"
                className={styles.arrowLink}
                href={data.ctaLink}
                iconAfter={LongArrowForwardIcon}
              >
                {data.ctaText}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
